import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { StudentBillingEventFeeItemDto } from "@madamuh/domain";
import {
  createStudentBillingEventFeeItem,
  fetchStudentBillingEventFee,
  fetchStudentBillingEventFees,
  updateStudentBillingEventFeeItem,
  type StudentBillingEventFeeListResponse,
  type StudentBillingEventFeeResponse
} from "../../shared/api/studentBillingConfiguration";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isUnauthenticatedApiError } from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import {
  BillingConfigurationNav,
  BillingStatusBadge,
  billingButtonStyle,
  billingCardStyle,
  billingGridStyle,
  billingInputStyle,
  billingMutedStyle,
  billingPageLinks,
  billingSecondaryButtonStyle,
  formatBillingDateTime,
  formatBillingYen,
  studentBillingConfigurationError
} from "./AdminBillingConfigurationShared";

function activeItems(item: StudentBillingEventFeeListResponse["items"][number]): StudentBillingEventFeeItemDto[] {
  return item.items.filter((entry) => entry.status === "active");
}

export function AdminBillingEventFeesPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const [response, setResponse] = useState<StudentBillingEventFeeListResponse | null>(null);
  const [safeError, setSafeError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    let active = true;
    setSafeError(null);
    void fetchStudentBillingEventFees(tenantSlug)
      .then((value) => { if (active) setResponse(value); })
      .catch((error: unknown) => {
        if (!active) return;
        if (isUnauthenticatedApiError(error)) { navigate(`/t/${tenantSlug}/entry`, { replace: true }); return; }
        setSafeError(studentBillingConfigurationError(error, "イベント料金設定を取得できませんでした。"));
      });
    return () => { active = false; };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  return (
    <OperatorPageScaffold currentArea="admin" tenantSlug={tenantSlug} tenantName={session?.tenant.displayName} session={session}
      title="イベント料金設定" description="運用イベントに複数の料金項目を設定します。" links={billingPageLinks} layoutVariant="wide">
      <div style={billingGridStyle}>
        <BillingConfigurationNav tenantSlug={tenantSlug} />
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {safeError ? <p role="alert">{safeError}</p> : null}
        {response ? (
          <>
            <p style={billingMutedStyle}>公開イベントではなく、管理用の運用イベントを料金設定の対象にしています。</p>
            {!response.capabilities.canManageFeeMasters ? <p style={billingMutedStyle}>閲覧のみです。料金を変更できるのはテナントオーナーです。</p> : null}
            {response.items.length === 0 ? <p>対象イベントはありません。</p> : null}
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))" }}>
              {response.items.map((item) => {
                const configured = activeItems(item);
                const allFree = configured.length > 0 && configured.every((entry) => entry.amount === 0);
                return (
                  <article key={item.eventId} style={billingCardStyle}>
                    <Link to={`/t/${tenantSlug}/teacher/admin/billing/event-fees/${item.eventId}`}><strong>{item.eventTitle}</strong></Link>
                    <p style={billingMutedStyle}>開催: {formatBillingDateTime(item.eventStartAt)}</p>
                    {!item.feeConfigured ? <BillingStatusBadge tone="warning">未設定</BillingStatusBadge>
                      : configured.length === 0 ? <BillingStatusBadge>有効な料金項目なし</BillingStatusBadge>
                      : allFree ? <BillingStatusBadge tone="success">無料（0円）</BillingStatusBadge>
                        : <BillingStatusBadge tone="success">設定済み</BillingStatusBadge>}
                    <p style={billingMutedStyle}>有効な料金項目: {configured.length}件 / 全履歴: {item.items.length}件</p>
                  </article>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}

type EventItemForm = {
  label: string;
  amount: string;
  billingMonthRule: "eventFirstMonth" | "explicitServiceMonth";
  explicitServiceMonth: string;
  status: "active" | "inactive";
};

const emptyForm: EventItemForm = {
  label: "",
  amount: "",
  billingMonthRule: "eventFirstMonth",
  explicitServiceMonth: "",
  status: "active"
};

function formFrom(item: StudentBillingEventFeeItemDto): EventItemForm {
  return {
    label: item.label,
    amount: String(item.amount),
    billingMonthRule: item.billingMonthRule,
    explicitServiceMonth: item.explicitServiceMonth ?? "",
    status: item.status
  };
}

export function AdminBillingEventFeeDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", eventId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const [detail, setDetail] = useState<StudentBillingEventFeeResponse | null>(null);
  const [form, setForm] = useState<EventItemForm>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [safeError, setSafeError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setSafeError(null);
    try { setDetail(await fetchStudentBillingEventFee(tenantSlug, eventId)); }
    catch (error) {
      if (isUnauthenticatedApiError(error)) { navigate(`/t/${tenantSlug}/entry`, { replace: true }); return; }
      setSafeError(studentBillingConfigurationError(error, "イベント料金設定を取得できませんでした。"));
    }
  }, [eventId, navigate, tenantSlug]);

  useEffect(() => { if (isSessionReady && hasAdminRole) void load(); }, [hasAdminRole, isSessionReady, load]);

  function edit(item: StudentBillingEventFeeItemDto) {
    setEditingId(item.feeItemId);
    setForm(formFrom(item));
    setSafeError(null);
    setMessage(null);
  }

  function reset() {
    setEditingId(null);
    setForm(emptyForm);
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail) return;
    setBusy(true);
    setSafeError(null);
    setMessage(null);
    try {
      const amount = Number(form.amount);
      if (!Number.isSafeInteger(amount) || amount < 0) throw new Error("invalid amount");
      const shared = {
        label: form.label.trim(),
        amount,
        currency: "JPY" as const,
        billingMonthRule: form.billingMonthRule,
        explicitServiceMonth: form.billingMonthRule === "explicitServiceMonth" ? form.explicitServiceMonth : null
      };
      if (editingId) {
        const current = detail.items.find((item) => item.feeItemId === editingId);
        if (!current) throw new Error("missing item");
        await updateStudentBillingEventFeeItem(tenantSlug, eventId, editingId, { ...shared, status: form.status, expectedVersion: current.version });
        setMessage("料金項目を更新しました。履歴は削除されません。");
      } else {
        await createStudentBillingEventFeeItem(tenantSlug, eventId, { ...shared, expectedVersion: null });
        setMessage("料金項目を追加しました。");
      }
      reset();
      await load();
    } catch (error) {
      setSafeError(studentBillingConfigurationError(error, "料金項目の入力内容を確認してください。"));
    } finally { setBusy(false); }
  }

  return (
    <OperatorPageScaffold currentArea="admin" tenantSlug={tenantSlug} tenantName={session?.tenant.displayName} session={session}
      title="イベント料金の詳細" description="イベントの料金項目と請求対象月を管理します。" links={billingPageLinks} layoutVariant="wide">
      <div style={billingGridStyle}>
        <BillingConfigurationNav tenantSlug={tenantSlug} />
        <Link to={`/t/${tenantSlug}/teacher/admin/billing/event-fees`}>イベント料金設定へ戻る</Link>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {safeError ? <p role="alert">{safeError}</p> : null}
        {message ? <p role="status">{message}</p> : null}
        {detail ? (
          <>
            <section style={billingCardStyle}>
              <h2 style={{ margin: 0 }}>{detail.eventTitle}</h2>
              <p style={billingMutedStyle}>開催: {formatBillingDateTime(detail.eventStartAt)}</p>
              {detail.chargeTargetReady === "notImplemented" ? (
                <p role="note" style={{ margin: 0, padding: 12, borderRadius: 8, background: "#fff4d8" }}>
                  料金設定は保存できますが、参加者への月次請求反映は今後の工程で実装されます。請求対象者の確定は未実装です。
                </p>
              ) : null}
            </section>

            <section style={billingCardStyle}>
              <h2 style={{ margin: 0 }}>料金項目</h2>
              {detail.items.length === 0 ? <p style={billingMutedStyle}>料金項目は未設定です。</p> : detail.items.map((item) => (
                <article key={item.feeItemId} style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10, display: "grid", gap: 5 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <strong>{item.label}: {formatBillingYen(item.amount)}</strong>
                    <BillingStatusBadge tone={item.status === "active" ? "success" : "neutral"}>{item.status === "active" ? "有効" : "無効"}</BillingStatusBadge>
                  </div>
                  <span style={billingMutedStyle}>請求対象月: {item.resolvedServiceMonthPreview}（{item.billingMonthRule === "eventFirstMonth" ? "イベント開催月" : "指定月"}）</span>
                  {detail.capabilities.canManageFeeMasters ? <button type="button" style={billingSecondaryButtonStyle} onClick={() => edit(item)}>編集・無効化</button> : null}
                </article>
              ))}
            </section>

            {detail.capabilities.canManageFeeMasters ? (
              <form onSubmit={(event) => void save(event)} style={billingCardStyle}>
                <h2 style={{ margin: 0 }}>{editingId ? "料金項目を編集" : "料金項目を追加"}</h2>
                <label style={billingGridStyle}><span>項目名</span><input required maxLength={80} value={form.label} onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))} style={billingInputStyle} /></label>
                <label style={billingGridStyle}><span>金額（円）</span><input aria-label="イベント料金（円）" required type="number" min="0" step="1" value={form.amount} onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))} style={billingInputStyle} /></label>
                <p style={billingMutedStyle}>0円は「無料」として保存されます。通貨は JPY 固定です。</p>
                <label style={billingGridStyle}>
                  <span>請求対象月の決め方</span>
                  <select value={form.billingMonthRule} onChange={(event) => setForm((current) => ({ ...current, billingMonthRule: event.target.value as EventItemForm["billingMonthRule"], explicitServiceMonth: "" }))} style={billingInputStyle}>
                    <option value="eventFirstMonth">イベント開催月</option>
                    <option value="explicitServiceMonth">対象月を指定</option>
                  </select>
                </label>
                {form.billingMonthRule === "explicitServiceMonth" ? <label style={billingGridStyle}><span>対象月</span><input type="month" required value={form.explicitServiceMonth} onChange={(event) => setForm((current) => ({ ...current, explicitServiceMonth: event.target.value }))} style={billingInputStyle} /></label> : null}
                {editingId ? (
                  <label style={billingGridStyle}><span>状態</span><select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as EventItemForm["status"] }))} style={billingInputStyle}><option value="active">有効</option><option value="inactive">無効</option></select></label>
                ) : null}
                <p style={billingMutedStyle}>料金項目は削除できません。不要になった項目は「無効」にして履歴を保持します。</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="submit" disabled={busy} style={billingButtonStyle}>{busy ? "保存中..." : editingId ? "料金項目を更新" : "料金項目を追加"}</button>
                  {editingId ? <button type="button" onClick={reset} style={billingSecondaryButtonStyle}>編集をキャンセル</button> : null}
                </div>
              </form>
            ) : <p style={billingMutedStyle}>閲覧のみです。料金を変更できるのはテナントオーナーです。</p>}
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
